import { useState } from 'react';
import { toast } from 'react-toastify';
import { toastOptions } from '../../tools/helpers/ToastHelper';
import { MarketComponentMarketComponentIOMarketComponentStatusEnum } from '../../generated/fresh-guard-api';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import type MarketComponentDetailsDialogProperties from './MarketComponentDetailsDialog.properties.ts';
import isAdmin from '../../security/IsAdmin.ts';
import isSupervisor from '../../security/IsSupervisor.ts';
import { extractMessageFromApi } from '../../tools/helpers/MessageHelper.ts';
import { useEditMarket } from '../../provider/edit-market/EditMarket.hook.ts';
import { useMarketComponentUpdateStatusDateOptimistic } from '../../apis/market-component/useMarketComponentUpdateStatusDateOptimistic.ts';

export const useMarketComponentDetailsDialogController = (
  props: MarketComponentDetailsDialogProperties
) => {
  const { t } = useTranslation();
  const [disabledYes, setDisabledYes] = useState(true);
  const [isAreYouSureDialogOpen, setAreYouSureDialogOpen] = useState(false);
  const [isHistoryDialogOpen, setHistoryDialogOpen] = useState(false);
  const [switchToRedAfterDate, setSwitchToRedAfterDate] = useState<string | null>(null);

  const { updateMarketComponentStatus } = useEditMarket();
  const { mutate, isPending } = useMarketComponentUpdateStatusDateOptimistic();

  const handleChangeShelfLifeClick = () => {
    setAreYouSureDialogOpen(true);
  };

  const handleYesClick = () => {
    if (props.marketComponent) {
      const updatedMarketComponent = {
        ...props.marketComponent,
        switchToRedAfterDate,
      };

      if (updatedMarketComponent.id) {
        mutate(
          {
            marketComponentId: updatedMarketComponent.id.toString(),
            marketComponentMarketComponentStatusDateInput: {
              date: switchToRedAfterDate,
            },
          },
          {
            onSuccess: (marketComponentData) => {
              toast.success(extractMessageFromApi(marketComponentData, t).message, toastOptions);

              if (updatedMarketComponent.marketComponentStatus) {
                const finalMarketComponent = {
                  ...updatedMarketComponent,
                  switchToRedAfterDate: switchToRedAfterDate,
                  marketComponentStatus:
                    marketComponentData.details as MarketComponentMarketComponentIOMarketComponentStatusEnum,
                };

                updateMarketComponentStatus(finalMarketComponent);
              }

              props.handleDialogCloseClick();
            },
          }
        );
      }
    }

    setAreYouSureDialogOpen(false);
  };

  const handleNoClick = () => {
    setAreYouSureDialogOpen(false);
  };

  const handleDialogGoToHistoryClick = () => {
    setHistoryDialogOpen(true);
  };

  const handleHistoryDialogCloseClick = () => {
    setHistoryDialogOpen(false);
  };

  const editSwitchToRedAfterDate = (value: Date | null) => {
    if (value) {
      const maxDate = new Date();
      maxDate.setDate(maxDate.getDate() + (props.marketComponent?.daysBeforeSwitchToRed ?? 0));

      if (!isAdmin() && !isSupervisor() && value < maxDate) {
        setDisabledYes(true);
        toast.error(t('ui.reduceArticleNow'), toastOptions);
      } else {
        setDisabledYes(false);
      }

      const now = new Date();
      const warnDate = new Date();
      warnDate.setDate(warnDate.getDate() + (props.marketComponent?.daysBeforeSwitchToRed ?? 0));

      if ((isAdmin() || isSupervisor()) && value > now && value < warnDate) {
        toast.error(t('ui.reduceArticleNow'), toastOptions);
      }

      const dateAsString = format(value, t('date.technicalDate'));
      setSwitchToRedAfterDate(dateAsString);
    } else {
      setDisabledYes(false);
      setSwitchToRedAfterDate(null);
    }
  };

  return {
    isAreYouSureDialogOpen,
    isHistoryDialogOpen,
    switchToRedAfterDate,
    handleChangeShelfLifeClick,
    handleYesClick,
    handleNoClick,
    handleDialogGoToHistoryClick,
    handleHistoryDialogCloseClick,
    editSwitchToRedAfterDate,
    loading: isPending,
    disabledYes,
  };
};
